import { useEffect, useState } from 'react'

interface NumberFieldProps {
  /** Current numeric value from the parent (source of truth). */
  value: number
  /** Called whenever the input contains a valid number. */
  onChange: (next: number) => void
  /** Minimum allowed value. Defaults to 0. */
  min?: number
  /** Value committed if the user blurs an empty / invalid input. */
  fallbackOnBlur?: number
  /** Pass-through styling props so the field can match its surrounding form. */
  className?: string
  style?: React.CSSProperties
  inputMode?: 'numeric' | 'decimal'
  autoFocus?: boolean
  disabled?: boolean
}

/**
 * Number input that allows the user to clear all digits while typing.
 * Internal state holds the raw string so empty strings are legal during
 * editing; the parent's numeric `value` is only updated when the input
 * parses to a finite number ≥ `min`.
 *
 * If the user blurs while the field is empty/invalid, we restore the
 * `fallbackOnBlur` (defaults to `value`), so the form never settles in
 * an undefined state.
 */
export function NumberField({
  value,
  onChange,
  min = 0,
  fallbackOnBlur,
  className,
  style,
  inputMode = 'numeric',
  autoFocus,
  disabled,
}: NumberFieldProps) {
  const [draft, setDraft] = useState<string>(String(value))

  // Sync external value changes (e.g. parent reset) into the draft.
  useEffect(() => {
    setDraft(String(value))
  }, [value])

  return (
    <input
      type="number"
      min={min}
      inputMode={inputMode}
      value={draft}
      autoFocus={autoFocus}
      onChange={(e) => {
        const raw = e.target.value
        setDraft(raw)
        const n = parseInt(raw, 10)
        if (Number.isFinite(n) && n >= min) {
          onChange(n)
        }
      }}
      onBlur={() => {
        const n = parseInt(draft, 10)
        if (!Number.isFinite(n) || n < min) {
          const restored = fallbackOnBlur ?? value
          setDraft(String(restored))
          onChange(restored)
        }
      }}
      className={className}
      style={style}
      disabled={disabled}
    />
  )
}
